import { useEffect, useState, useCallback } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useToast } from '@/hooks/useToast'
import ChangePasswordModal from '@/components/common/ChangePasswordModal'
import { 
  CogIcon,
  MoonIcon,
  SunIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  KeyIcon,
  CodeBracketIcon,
  PaintBrushIcon,
  FolderOpenIcon
} from '@heroicons/react/24/outline'

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', icon: SunIcon },
  { value: 'dark', label: 'Dark', icon: MoonIcon }
] as const


const FONT_FAMILY_OPTIONS = [
  { value: 'Monaco, "Lucida Console", monospace', label: 'Monaco' },
  { value: '"Fira Code", monospace', label: 'Fira Code' },
  { value: '"Source Code Pro", monospace', label: 'Source Code Pro' },
  { value: '"JetBrains Mono", monospace', label: 'JetBrains Mono' },
  { value: '"Courier New", monospace', label: 'Courier New' },
  { value: 'Consolas, monospace', label: 'Consolas' }
]

const ROW_COUNT_OPTIONS = [
  { value: 25, label: '25 rows' },
  { value: 50, label: '50 rows' },
  { value: 100, label: '100 rows' },
  { value: 200, label: '200 rows' },
  { value: 500, label: '500 rows' }
]

export function Settings() {
  const { user } = useAuthStore()
  const { settings, isLoading, error, updateSetting } = useSettingsStore()
  const { showToast } = useToast()
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false)

  // Settings are already fetched by useAppSettings hook in App.tsx

  useEffect(() => {
    if (error) {
      showToast('error', error)
    }
  }, [error, showToast])

  const handleSettingChange = async <K extends keyof typeof settings>(
    key: K, 
    value: typeof settings[K]
  ) => {
    setSaveStatus('saving')
    try {
      await updateSetting(key, value)
      setSaveStatus('saved')
      showToast('success', 'Setting saved automatically')
      
      // Reset save status after 2 seconds
      setTimeout(() => { setSaveStatus('idle') }, 2000)
    } catch {
      setSaveStatus('error')
      setTimeout(() => { setSaveStatus('idle') }, 2000)
    }
  }

  // Wrapper functions to handle promises properly for event handlers
  const handleThemeChange = (value: 'light' | 'dark') => {
    void handleSettingChange('theme', value)
  }


  const handleAutoCompleteToggle = () => {
    void handleSettingChange('autoComplete', !settings.autoComplete)
  }

  const handleLineNumbersToggle = () => {
    void handleSettingChange('showLineNumbers', !settings.showLineNumbers)
  }

  const handleFontFamilyChange = (value: string) => {
    void handleSettingChange('fontFamily', value)
  }

  const handleFontSizeChange = (value: number) => {
    void handleSettingChange('fontSize', value)
  }

  const handleQueryLimitChange = (value: number) => {
    void handleSettingChange('queryLimit', value)
  }

  const handleKeepExplorerExpandedToggle = () => {
    void handleSettingChange('keepExplorerExpandedState', !settings.keepExplorerExpandedState)
  }

  const handleExplorerCompactViewToggle = () => {
    void handleSettingChange('explorerCompactView', !settings.explorerCompactView)
  }

  const handleCloseChangePasswordModal = useCallback(() => {
    setIsChangePasswordModalOpen(false)
  }, [])

  const SaveStatusIndicator = () => {
    if (saveStatus === 'idle') return null
    
    const statusConfig = {
      saving: { text: 'Saving...', className: 'text-yellow-600' },
      saved: { text: 'Saved', className: 'text-green-600' },
      error: { text: 'Save failed', className: 'text-red-600' }
    }
    
    const config = statusConfig[saveStatus]
    
    return (
      <div className={`text-sm font-medium ${config.className}`}>
        {config.text}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="overflow-y-auto max-h-screen">
      <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Configure your application preferences - changes are saved automatically
          </p>
        </div>
        <SaveStatusIndicator />
      </div>

      <div className="space-y-6">
        {/* Profile Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <CogIcon className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Profile</h2>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {user?.authProvider && user.authProvider !== 'local' ? 'Email' : 'Username'}
            </label>
            <input
              type="text"
              value={user?.authProvider && user.authProvider !== 'local' ? (user?.email || user?.username || '') : (user?.username || '')}
              readOnly
              className="input w-full max-w-md bg-gray-50 dark:bg-gray-700"
            />
          </div>
        </div>

        {/* Appearance */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <PaintBrushIcon className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Appearance</h2>
          </div>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Theme
              </label>
              <div className="grid grid-cols-3 gap-3">
                {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
                  <button type="button"
                    key={value}
                    onClick={() => { handleThemeChange(value) }}
                    className={`p-3 rounded-lg border transition-all duration-200 ${
                      settings.theme === value
                        ? 'bg-primary-50 border-primary-200 ring-2 ring-primary-500 dark:bg-primary-900/20 dark:border-primary-700'
                        : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600'
                    }`}
                  >
                    <Icon className="w-6 h-6 mx-auto mb-2 text-gray-600 dark:text-gray-400" />
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {label}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Query Editor */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <CodeBracketIcon className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Query Editor</h2>
          </div>
          
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Auto Complete
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Enable suggestions
                  </div>
                </div>
                <button type="button"
                  onClick={handleAutoCompleteToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    settings.autoComplete ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.autoComplete ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Line Numbers
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Show line numbers
                  </div>
                </div>
                <button type="button"
                  onClick={handleLineNumbersToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    settings.showLineNumbers ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.showLineNumbers ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Font Family
                </label>
                <select
                  value={settings.fontFamily}
                  onChange={(e) => { handleFontFamilyChange(e.target.value) }}
                  className="input w-full"
                >
                  {FONT_FAMILY_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Font Size
                </label>
                <input
                  type="number"
                  value={settings.fontSize}
                  onChange={(e) => { handleFontSizeChange(Number(e.target.value)) }}
                  min="10"
                  max="24"
                  className="input w-full"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Explorer */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <FolderOpenIcon className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Explorer</h2>
          </div>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Query Limit
              </label>
              <select
                value={settings.queryLimit}
                onChange={(e) => { handleQueryLimitChange(Number(e.target.value)) }}
                className="input w-full max-w-sm"
              >
                {ROW_COUNT_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Maximum number of rows to display when viewing table data
              </p>
            </div>

            <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Keep Expanded State
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Preserve nested items expanded when collapsing parent
                </div>
              </div>
              <button type="button"
                onClick={handleKeepExplorerExpandedToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  settings.keepExplorerExpandedState ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.keepExplorerExpandedState ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Compact View
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Hide rows and size columns in table lists
                </div>
              </div>
              <button type="button"
                onClick={handleExplorerCompactViewToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  settings.explorerCompactView ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.explorerCompactView ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Security - Only show for non-SSO users */}
        {(!user?.authProvider || user.authProvider === 'local') && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <ShieldCheckIcon className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Security</h2>
            </div>
            
            <div className="space-y-4">
              <button type="button" 
                onClick={() => { setIsChangePasswordModalOpen(true) }}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors duration-200 space-x-2"
              >
                <KeyIcon className="w-4 h-4" />
                <span>Change Password</span>
              </button>
            </div>
          </div>
        )}

        {/* About */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <GlobeAltIcon className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">About</h2>
          </div>
          
          <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex justify-between">
              <span>Application Version:</span>
              <span className="font-medium">{import.meta.env.VITE_APP_VERSION || '0.0.102'}</span>
            </div>
            <div className="flex justify-between">
              <span>Build Date:</span>
              <span className="font-medium">{import.meta.env.VITE_BUILD_DATE || '8/21/2025'}</span>
            </div>
            <div className="flex justify-between">
              <span>License:</span>
              <span className="font-medium">Proprietary</span>
            </div>
            <div className="flex justify-between">
              <span>Support:</span>
              <a 
                href="mailto:support@mazpin.com" 
                className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
              >
                support@mazpin.com
              </a>
            </div>
          </div>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={handleCloseChangePasswordModal}
      />
      </div>
    </div>
  )
}
